
/**
 * Core SEO utilities for basic meta tag management
 */

interface MetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  language?: string;
}

/**
 * Updates the meta tags in the document head based on provided values
 */
export const updateMetaTags = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "https://future-level.ch/opengraph-image-p98pqg.png", // Default OG image
  ogType = "website",
  language = "de", // Default language
}: MetaTagsProps): void => {
  // Basic meta tags
  document.title = title;
  
  // Update description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  } else {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', description);
    document.head.appendChild(metaDescription);
  }

  // Update keywords if provided
  if (keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords);
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', keywords);
      document.head.appendChild(metaKeywords);
    }
  }

  // Update language attribute on html tag
  document.documentElement.setAttribute('lang', language);
  
  // Add viewport meta tag if not exists (essential for responsive design and SEO)
  let viewportTag = document.querySelector('meta[name="viewport"]');
  if (!viewportTag) {
    viewportTag = document.createElement('meta');
    viewportTag.setAttribute('name', 'viewport');
    viewportTag.setAttribute('content', 'width=device-width, initial-scale=1.0');
    document.head.appendChild(viewportTag);
  }
  
  // Add robots meta tag (informs search engines how to handle the page)
  let robotsTag = document.querySelector('meta[name="robots"]');
  if (!robotsTag) {
    robotsTag = document.createElement('meta');
    robotsTag.setAttribute('name', 'robots');
    robotsTag.setAttribute('content', 'index, follow, max-image-preview:large');
    document.head.appendChild(robotsTag);
  }

  // Update canonical URL if provided
  if (canonicalUrl) {
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonicalLink);
    }
  }
};

/**
 * Generates a canonical URL for the current page
 */
export const getCanonicalUrl = (path: string, language: string): string => {
  const baseUrl = 'https://www.future-level.ch';
  return `${baseUrl}/${language}${path ? `/${path}` : ''}`;
};
